import { getAvatar } from '$tablet-app/src/app/avatars/functions/getAvatar';
import AvatarBubble from '$tablet-app/src/app/components/AvatarBubble';
import BlurredBackground from '$tablet-app/src/app/components/BlurredBackground';
import { EllipseAnimation } from '$tablet-app/src/app/components/EllipsisAnimation/EllipsisAnimation';
import { startVoiceListening } from '$tablet-app/src/app/speech-to-text/useSTT';
import { useGetStateVariable } from '$tablet-app/src/app/state_variables';
import { StateVariableNames } from '$tablet-app/src/app/state_variables/state_variable_names';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useEffect, useState } from 'react';
import { TouchableOpacity, useWindowDimensions } from 'react-native';
import Draggable from 'react-native-draggable';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import { shallow } from 'zustand/shallow';
import { ChatItemContainer } from '../../ChatItem';
import { ChatItem } from '../../ChatItem/types';
import ChatModal from '../../ChatModal';
import { FocusedChatWindow } from '../../focused_chat/components/FocusedChatWindow';
import { useFocusedChatState } from '../../focused_chat/hooks/useFocusedChatState';
import { isOutputChatItem } from '../../functions';
import { useAvatarBubble } from '../../hooks/useAvatarBubble';
import { ChatItemDialog } from '../ChatItemDialog';
import { ChatTextBubble } from '../ChatTextBubble';

interface Props {
  children: React.ReactNode;
}

/** The ChatActionsLayer sits over the app and provides functionality for popovers, avatar bubble and more. */
export const ChatActionsLayer = (props: Props) => {
  const chat_items = useStateStore((state) => state.chat_items);

  const [
    listening,
    waiting_for_response,
    chatVisible,
    visible_text_item_idx,
    visible_dynamic_item,
    showChat,
  ] = useStateStore(
    (state) => [
      state.listening_to_voice,
      state.waiting_for_response,
      state.chat_dialog_visible,
      state.displayed_chat_item_idx,
      state.displayed_dynamic_chat_item,
      state.showChatDialog,
    ],
    shallow
  );

  const focused_chat_visible = useFocusedChatState((state) => state.is_visible);

  const { data, isLoading } = useGetStateVariable(StateVariableNames.AVATAR);
  const avatar_asset = getAvatar(data);

  const { initial_location, onDragRelease } = useAvatarBubble();

  const visible_chat_item = findVisibleChatItem(
    chat_items,
    visible_text_item_idx
  );

  const { width, height } = useWindowDimensions();

  return (
    <>
      {/* The rest of the app */}
      {props.children}

      {!focused_chat_visible ? (
        <>
          <FadedBackground visible={visible_text_item_idx !== undefined} />

          {/* Text speech bubble */}
          {visible_text_item_idx !== undefined &&
          visible_chat_item &&
          !waiting_for_response ? (
            <ChatTextBubble
              item_idx={visible_text_item_idx}
              item={visible_chat_item}
            />
          ) : null}

          {waiting_for_response ? (
            <TextBubble>
              <EllipseAnimation />
            </TextBubble>
          ) : null}

          {/* Avatar Bubble */}
          {!isLoading ? (
            <Draggable
              z={1000}
              x={initial_location.x}
              y={initial_location.y}
              onDragRelease={onDragRelease}
            >
              <TouchableOpacity onPress={startVoiceListening}>
                <AvatarBubble
                  avatar_image_asset={avatar_asset}
                  listening={listening}
                />

                <OpenChatButton onPress={showChat}>
                  <FontAwesomeIcon icon="up-right-and-down-left-from-center" />
                </OpenChatButton>
              </TouchableOpacity>
            </Draggable>
          ) : null}

          {/* Notification */}
          {visible_dynamic_item ? (
            <AbsoluteWrapper
              pointerEvents="box-none"
              style={{ maxHeight: 0.6 * height }}
            >
              <ContainerDialog
                style={{ maxWidth: Math.max(0.9 * 320, 0.6 * width) }}
              >
                <ChatItemDialog chat_item={visible_dynamic_item} />
              </ContainerDialog>
            </AbsoluteWrapper>
          ) : null}

          {chatVisible ? <ChatModal /> : null}
        </>
      ) : (
        <FocusedChatWindow />
      )}
    </>
  );
};

const TextBubble = styled.View`
  position: absolute;

  bottom: 90px;
  right: 130px;

  background-color: blue;
  border-radius: 10px;
  border-bottom-right-radius: 0px;

  background-color: rgba(5, 22, 51, 0.8);

  max-width: 420px;
`;

const OpenChatButton = styled.TouchableOpacity`
  position: absolute;

  top: -15px;
  right: -15px;

  background-color: white;

  height: 30px;
  width: 30px;

  align-items: center;
  justify-content: center;
  border-radius: 20px;
`;

const AbsoluteWrapper = styled.View`
  position: absolute;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  top: 40px;
`;

const ContainerDialog = styled(ChatItemContainer)`
  flex: 1;
`;

const WhiteBackground = styled(BlurredBackground)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
`;

const AnimatedBlur = styled(Animated.View)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;

const TouchableBlur = styled.TouchableOpacity`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;

const FadedBackground = ({ visible }: { visible?: boolean }) => {
  const [show, setShow] = useState(false);
  const hide = () => setShow(false);

  const opacity = useSharedValue(0);

  const style = useAnimatedStyle(() => ({
    opacity: withTiming(opacity.value, {
      duration: 400,
      easing: Easing.inOut(Easing.ease),
    }),
  }));

  useEffect(() => {
    if (visible) {
      setShow(true);
      opacity.value = 1;
    } else {
      opacity.value = 0;
      setTimeout(() => setShow(false), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return show ? (
    <AnimatedBlur style={style}>
      <WhiteBackground
        blurType="light"
        blurAmount={1}
        reducedTransparencyFallbackColor="rgba(255, 255, 255, 0.7)"
      >
        <TouchableBlur onPress={hide} />
      </WhiteBackground>
    </AnimatedBlur>
  ) : null;
};

function findVisibleChatItem(items: ChatItem[], idx?: number) {
  if (idx === undefined) return;

  const item = items[idx];

  if (item && isOutputChatItem(item)) {
    return item;
  }
}
